import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Unified Button component with all variants.
 *
 * Standard variants (Shadcn defaults):
 * - default, destructive, outline, secondary, ghost, link
 *
 * Hero variants (for CTAs and landing pages):
 * - hero-primary: Green gradient, large, rounded-full
 * - hero-secondary: White with green text, rounded-full
 * - hero-outline: Transparent with border, rounded-full
 *
 * Utility variants:
 * - tactile: Adds scale effect on hover/active (combine with other variants via className)
 *
 * All hero variants include the tactile effect by default.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 max-w-full whitespace-normal rounded-md text-center text-sm font-medium ring-offset-background transition duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Standard Shadcn variants
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs hover:shadow-md active:scale-[0.98]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-xs hover:shadow-md active:scale-[0.98]",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground shadow-xs hover:shadow-md active:scale-[0.98]",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-xs hover:shadow-md active:scale-[0.98]",
        ghost: "hover:bg-accent hover:text-accent-foreground active:scale-[0.98]",
        link: "text-primary underline-offset-4 hover:underline",

        // Hero variants for CTAs (include tactile effect)
        "hero-primary":
          "group relative bg-linear-to-r from-green-700 to-emerald-700 hover:from-green-800 hover:to-emerald-800 text-white rounded-full font-bold shadow-2xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-green-200 overflow-hidden",
        "hero-secondary":
          "group bg-white text-action hover:bg-gray-50 rounded-full font-bold shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-green-200",
        "hero-outline":
          "group border-2 border-white/30 text-white hover:bg-white/10 rounded-full font-bold transform hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-green-200",

        // Green variants (common in lawn care theme)
        "green":
          "bg-action hover:bg-action-hover text-action-foreground font-bold rounded-full shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-green-200",
        "green-outline":
          "border-2 border-green-300 text-green-700 hover:bg-green-50 bg-white font-semibold rounded-full transform hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-green-200",
      },
      size: {
        default: "min-h-11 px-4 py-2 text-sm",
        sm: "min-h-11 rounded-md px-3 py-2 text-xs",
        lg: "min-h-12 rounded-md px-8 py-2 text-base",
        xl: "min-h-14 rounded-md px-12 py-2 text-lg",
        icon: "h-11 w-11",
        // Hero sizes (for landing page CTAs)
        "hero": "min-h-[56px] px-8 md:px-12 py-4 md:py-6 text-lg md:text-xl",
        "hero-lg": "min-h-[64px] px-10 md:px-16 py-5 md:py-7 text-xl md:text-2xl",
      },
      // Optional tactile effect (scale on hover/active)
      tactile: {
        true: "transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      tactile: false,
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Optional tactile effect (scale on hover/active). Already included in hero variants. */
  tactile?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, tactile, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, tactile, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
